import React, { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import AuthService from '../services/AuthService';
import { useDropzone } from 'react-dropzone';
import {
    Box, Button, Typography, LinearProgress, Snackbar, Alert, Dialog,
    DialogActions, DialogContent, TextField, Menu, MenuItem, IconButton,
    Chip, Tooltip, Divider,
} from '@mui/material';
import { styled } from '@mui/system';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import LinkIcon from '@mui/icons-material/Link';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import fileService from '../services/FileService';
import { useTranslation } from 'react-i18next';

const BRAND = '#287bd6';
const BRAND_DARK = '#1f6fca';
const BRAND_GRADIENT = 'linear-gradient(135deg, #287bd6 0%, #1f6fca 100%)';

const primaryBtnSx = {
    background: BRAND_GRADIENT,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.9rem',
    borderRadius: '9px',
    minHeight: 42,
    px: 2.6,
    boxShadow: '0 2px 8px rgba(40,123,214,0.32)',
    '&:hover': {
        background: 'linear-gradient(135deg, #3f8fe0 0%, #287bd6 100%)',
        boxShadow: '0 3px 12px rgba(40,123,214,0.4)',
    },
    '&.Mui-disabled': {
        background: '#c9d6e5',
        color: '#fff',
        boxShadow: 'none',
    },
};

const cancelBtnSx = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    borderRadius: '9px',
    minHeight: 42,
    px: 2.2,
    color: 'text.secondary',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
};

const DropzoneContainer = styled('div', { shouldForwardProp: (prop) => prop !== 'isDragActive' })(
    ({ isDragActive }) => ({
        width: '100%',
        minHeight: '210px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 6,
        padding: '28px 20px',
        border: `2px dashed ${isDragActive ? BRAND : '#cdd7e3'}`,
        borderRadius: 14,
        backgroundColor: isDragActive ? 'rgba(40,123,214,0.06)' : '#f7f9fc',
        cursor: 'pointer',
        textAlign: 'center',
        outline: 'none',
        transition: 'border-color .2s ease, background-color .2s ease, transform .2s ease',
        '&:hover': {
            borderColor: BRAND,
            backgroundColor: 'rgba(40,123,214,0.04)',
        },
    })
);

const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const fileIconFor = (fileName = '') => {
    const ext = fileName.split('.').pop().toLowerCase();
    const common = { fontSize: 22 };
    if (ext === 'pdf') return <PictureAsPdfOutlinedIcon sx={{ ...common, color: '#e0554e' }} />;
    if (ext === 'csv' || ext === 'xlsx' || ext === 'xls') return <TableChartOutlinedIcon sx={{ ...common, color: '#2f9e6f' }} />;
    if (ext === 'docx' || ext === 'doc') return <DescriptionOutlinedIcon sx={{ ...common, color: BRAND }} />;
    if (ext === 'txt') return <ArticleOutlinedIcon sx={{ ...common, color: '#6b7684' }} />;
    return <InsertDriveFileOutlinedIcon sx={{ ...common, color: '#6b7684' }} />;
};

// ---- Reusable modal header ----
function ModalHeader({ icon, title, subtitle, onClose }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, px: 3, pt: 2.75, pb: 2 }}>
            <Box
                aria-hidden
                sx={{
                    flexShrink: 0,
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(40,123,214,0.1)',
                    color: BRAND,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.12rem', color: '#1a2634', lineHeight: 1.3 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography sx={{ mt: 0.25, fontSize: '0.85rem', color: 'text.secondary' }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
            <IconButton
                onClick={onClose}
                size="small"
                aria-label="close"
                sx={{ mt: -0.5, mr: -0.75, color: 'text.secondary' }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}

// ---- Reusable upload zone ----
function UploadZone({ rootProps, inputProps, isDragActive, title, dropTitle, formats, hint }) {
    return (
        <DropzoneContainer {...rootProps} isDragActive={isDragActive}>
            <input {...inputProps} />
            <Box
                aria-hidden
                sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isDragActive ? 'rgba(40,123,214,0.14)' : 'rgba(40,123,214,0.08)',
                    color: BRAND,
                    mb: 0.5,
                    transition: 'background-color .2s ease',
                }}
            >
                <CloudUploadIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.98rem', color: '#2b3a4b', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                {isDragActive ? dropTitle : title}
            </Typography>
            {formats?.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', mt: 0.75 }}>
                    {formats.map((f) => (
                        <Chip
                            key={f}
                            label={f}
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: BRAND_DARK,
                                backgroundColor: 'rgba(40,123,214,0.1)',
                                borderRadius: '6px',
                            }}
                        />
                    ))}
                </Box>
            )}
            {hint && (
                <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', fontSize: '0.8rem' }}>
                    {hint}
                </Typography>
            )}
        </DropzoneContainer>
    );
}

// ---- Reusable selected-files list ----
function SelectedFiles({ files, progress, uploading, onRemove, t }) {
    if (!files.length) return null;
    return (
        <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('files.selectedFiles', 'Selected files')} ({files.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 200, overflowY: 'auto', pr: 0.5 }}>
                {files.map((file) => {
                    const pct = progress[file.name] || 0;
                    const done = pct >= 100;
                    return (
                        <Box
                            key={file.name}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                p: 1.25,
                                borderRadius: '10px',
                                border: '1px solid #e6ebf1',
                                backgroundColor: '#fff',
                            }}
                        >
                            <Box sx={{ flexShrink: 0, display: 'flex' }}>{fileIconFor(file.name)}</Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#2b3a4b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.name}
                                </Typography>
                                {uploading ? (
                                    <LinearProgress
                                        variant="determinate"
                                        value={pct}
                                        sx={{ mt: 0.75, height: 5, borderRadius: 3, backgroundColor: '#eef2f6', '& .MuiLinearProgress-bar': { backgroundColor: BRAND } }}
                                    />
                                ) : (
                                    <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mt: 0.1 }}>
                                        {formatBytes(file.size)}
                                    </Typography>
                                )}
                            </Box>
                            {uploading ? (
                                done ? (
                                    <CheckCircleIcon sx={{ color: '#2f9e6f', fontSize: 20 }} />
                                ) : (
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND, minWidth: 34, textAlign: 'right' }}>
                                        {pct}%
                                    </Typography>
                                )
                            ) : (
                                <Tooltip title={t('files.removeFile', 'Remove')}>
                                    <IconButton
                                        size="small"
                                        aria-label={t('files.removeFile', 'Remove')}
                                        onClick={() => onRemove(file.name)}
                                        sx={{ color: 'text.secondary', '&:hover': { color: '#e0554e' } }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

const dialogPaperSx = {
    borderRadius: '16px',
    boxShadow: '0 24px 60px rgba(16,36,64,0.22)',
    overflow: 'hidden',
};

function MultipleFileUpload({ onUploadComplete, baseId }) {
    const maxDocumentFileSize = 10 * 1024 * 1024;
    const [files, setFiles] = useState([]);
    const [filesTable, setFilesTable] = useState([]);
    const [filesOcr, setFilesOcr] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadingTable, setUploadingTable] = useState(false);
    const [uploadingOcr, setUploadingOcr] = useState(false);
    const [progress, setProgress] = useState({});
    const [progressTable, setProgressTable] = useState({});
    const [progressOcr, setProgressOcr] = useState({});
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [openDocsDialog, setOpenDocsDialog] = useState(false);
    const [openTableDialog, setOpenTableDialog] = useState(false);
    const [openOcrDialog, setOpenOcrDialog] = useState(false);
    const [openUrlDialog, setOpenUrlDialog] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [urlInput, setUrlInput] = useState('');
    const [urlRefresh, setUrlRefresh] = useState('Never');
    const [addingUrl, setAddingUrl] = useState(false);
    const { t } = useTranslation();

    const onDrop = useCallback((acceptedFiles) => {
        setFiles((prev) => {
            const map = new Map(prev.map((f) => [f.name, f]));
            acceptedFiles.forEach((f) => map.set(f.name, f));
            return Array.from(map.values());
        });
        setProgress({});
    }, []);

    const onDropRejected = useCallback(() => {
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('files.documentsAcceptedTypes'));
    }, [t]);

    const onDropOcr = useCallback((acceptedFiles) => {
        setFilesOcr((prev) => {
            const map = new Map(prev.map((f) => [f.name, f]));
            acceptedFiles.forEach((f) => map.set(f.name, f));
            return Array.from(map.values());
        });
        setProgressOcr({});
    }, []);

    const onDropTable = useCallback((acceptedFiles) => {
        setFilesTable((prev) => {
            const map = new Map(prev.map((f) => [f.name, f]));
            acceptedFiles.forEach((f) => map.set(f.name, f));
            return Array.from(map.values());
        });
        setProgressTable({});
    }, []);

    const onDropOcrRejected = useCallback(() => {
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('files.ocrPdfOnly'));
    }, [t]);

    const onDropTableRejected = useCallback(() => {
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('files.tableAcceptedTypes'));
    }, [t]);

    const handleCloseSnackbar = () => setOpenSnackbar(false);

    const handleOpenMenu = (event) => setMenuAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setMenuAnchorEl(null);

    const removeFile = (name) => setFiles((prev) => prev.filter((f) => f.name !== name));
    const removeFileTable = (name) => setFilesTable((prev) => prev.filter((f) => f.name !== name));
    const removeFileOcr = (name) => setFilesOcr((prev) => prev.filter((f) => f.name !== name));

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: {
            'application/pdf': ['.pdf'],
            'text/plain': ['.txt'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        },
        maxSize: maxDocumentFileSize,
        multiple: true,
    });

    const { getRootProps: getOcrRootProps, getInputProps: getOcrInputProps, isDragActive: isOcrDragActive } = useDropzone({
        onDrop: onDropOcr,
        onDropRejected: onDropOcrRejected,
        accept: {
            'application/pdf': ['.pdf'],
        },
        multiple: true,
    });

    const { getRootProps: getTableRootProps, getInputProps: getTableInputProps, isDragActive: isTableDragActive } = useDropzone({
        onDrop: onDropTable,
        onDropRejected: onDropTableRejected,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        },
        maxSize: maxDocumentFileSize,
        multiple: true,
    });

    const handleUpload = async () => {
        if (!files.length) return;

        setUploading(true);
        setProgress({});

        const token = AuthService.getAccessToken();
        if (!token) {
            setUploading(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('files.authRequired') || 'Authentication required');
            return;
        }

        try {
            const fileData = files.map((file) => ({
                fileName: file.name,
                fileType: file.type,
            }));

            const data = await fileService.generatePresign(baseId, fileData);

            const uploadPromises = files.map((file, index) => {
                const url = data[index];

                return axios.put(url, file, {
                    headers: { 'Content-Type': file.type },
                    onUploadProgress: (progressEvent) => {
                        const progressPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setProgress((prevProgress) => ({
                            ...prevProgress,
                            [file.name]: progressPercentage,
                        }));
                    },
                });
            });

            await Promise.all(uploadPromises);

            setUploading(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('success');
            setSnackbarMessage(t('files.uploadSuccess') || 'Upload completed');
            onUploadComplete();
            setFiles([]);
            setProgress({});
            setOpenDocsDialog(false); // Close dialog after successful upload

        } catch (error) {
            console.error('Error uploading files:', error);
            setUploading(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(error?.message || t('files.uploadError') || 'Error uploading files');
        }
    };

    const handleUploadOcr = async () => {
        if (!filesOcr.length) return;

        setUploadingOcr(true);
        setProgressOcr({});

        const token = AuthService.getAccessToken();
        if (!token) {
            setUploadingOcr(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('files.authRequired') || 'Authentication required');
            return;
        }

        try {
            const fileData = filesOcr.map((file) => ({
                fileName: file.name,
                fileType: file.type,
            }));

            const data = await fileService.generatePresign(baseId, fileData, { requiresOcr: true });

            const uploadPromises = filesOcr.map((file, index) => {
                const url = data[index];
                return axios.put(url, file, {
                    headers: { 'Content-Type': file.type },
                    onUploadProgress: (progressEvent) => {
                        const progressPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setProgressOcr((prevProgress) => ({
                            ...prevProgress,
                            [file.name]: progressPercentage,
                        }));
                    },
                });
            });

            await Promise.all(uploadPromises);

            setUploadingOcr(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('success');
            setSnackbarMessage(t('files.uploadSuccess') || 'Upload completed');
            onUploadComplete();
            setFilesOcr([]);
            setProgressOcr({});
            setOpenOcrDialog(false);
        } catch (error) {
            console.error('Error uploading OCR files:', error);
            setUploadingOcr(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(error?.message || t('files.uploadError') || 'Error uploading files');
        }
    };

    const handleUploadTable = async () => {
        if (!filesTable.length) return;

        setUploadingTable(true);
        setProgressTable({});

        const token = AuthService.getAccessToken();
        if (!token) {
            setUploadingTable(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('files.authRequired') || 'Authentication required');
            return;
        }

        try {
            const fileData = filesTable.map((file) => ({
                fileName: file.name,
                fileType: file.type,
            }));

            const data = await fileService.generatePresign(baseId, fileData);

            const uploadPromises = filesTable.map((file, index) => {
                const url = data[index];
                return axios.put(url, file, {
                    headers: { 'Content-Type': file.type },
                    onUploadProgress: (progressEvent) => {
                        const progressPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setProgressTable((prevProgress) => ({
                            ...prevProgress,
                            [file.name]: progressPercentage,
                        }));
                    },
                });
            });

            await Promise.all(uploadPromises);

            setUploadingTable(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('success');
            setSnackbarMessage(t('files.uploadSuccess') || 'Upload completed');
            onUploadComplete();
            setFilesTable([]);
            setProgressTable({});
            setOpenTableDialog(false);
        } catch (error) {
            console.error('Error uploading table files:', error);
            setUploadingTable(false);
            setOpenSnackbar(true);
            setSnackbarSeverity('error');
            setSnackbarMessage(error?.message || t('files.uploadError') || 'Error uploading files');
        }
    };

    const isValidUrl = (value) => {
        try {
            const u = new URL(value);
            return !!u.protocol && !!u.host;
        } catch {
            return false;
        }
    };

    const urlLines = useMemo(
        () => (urlInput || '').split('\n').map((l) => l.trim()).filter((l) => l.length > 0),
        [urlInput]
    );
    const validUrlCount = useMemo(() => urlLines.filter(isValidUrl).length, [urlLines]);
    const invalidUrlCount = urlLines.length - validUrlCount;

    const handleAddUrl = async () => {
        const lines = urlLines;

        if (!lines.length) {
            setSnackbarSeverity('error');
            setSnackbarMessage(t('files.urlEnterAtLeastOne', 'Please enter at least one URL'));
            setOpenSnackbar(true);
            return;
        }

        const invalid = lines.filter((l) => !isValidUrl(l));
        if (invalid.length) {
            setSnackbarSeverity('error');
            setSnackbarMessage(t('files.urlInvalidList', { urls: invalid.join(', '), defaultValue: `Invalid URL(s): ${invalid.join(', ')}` }));
            setOpenSnackbar(true);
            return;
        }

        setAddingUrl(true);
        try {
            for (const url of lines) {
                // add each URL one by one
                // eslint-disable-next-line no-await-in-loop
                await fileService.addUrl(baseId, url, { refresh: urlRefresh });
            }
            setSnackbarSeverity('success');
            setSnackbarMessage(t('files.urlAddSuccess', 'URL(s) added successfully'));
            setOpenSnackbar(true);
            setUrlInput('');
            setUrlRefresh('Never');
            if (onUploadComplete) onUploadComplete();
            setOpenUrlDialog(false);
        } catch (e) {
            console.error('Error adding URLs:', e);
            setSnackbarSeverity('error');
            setSnackbarMessage(e?.message || t('files.urlAddError', 'Failed to add URL(s)'));
            setOpenSnackbar(true);
        } finally {
            setAddingUrl(false);
        }
    };

    const closeDocs = () => { setFiles([]); setProgress({}); setOpenDocsDialog(false); };
    const closeTable = () => { setFilesTable([]); setProgressTable({}); setOpenTableDialog(false); };
    const closeOcr = () => { setFilesOcr([]); setProgressOcr({}); setOpenOcrDialog(false); };
    const closeUrl = () => { setUrlInput(''); setOpenUrlDialog(false); };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenMenu}
                sx={{
                    background: BRAND,
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    borderRadius: '7px',
                    minHeight: 44,
                    px: 2.1,
                    boxShadow: '0 2px 5px rgba(0,0,0,0.25)',
                    '& .MuiButton-startIcon': {
                        mr: 1,
                        '& .MuiSvgIcon-root': {
                            fontSize: 20,
                        },
                    },
                    '&:hover': {
                        background: BRAND_DARK,
                        boxShadow: '0 3px 7px rgba(0,0,0,0.28)',
                    },
                }}
            >
                {t('files.addDocument', 'Document')}
            </Button>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
                PaperProps={{ sx: { mt: 1, borderRadius: '12px', minWidth: 240, boxShadow: '0 12px 32px rgba(16,36,64,0.16)', py: 0.5 } }}
            >
                <MenuItem onClick={() => { setOpenDocsDialog(true); handleCloseMenu(); }} sx={{ gap: 1.5, py: 1.1, px: 2 }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 20, color: BRAND }} />
                    <Typography sx={{ fontSize: '0.9rem' }}>{t('files.addDocuments', 'Add Documents')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => { setOpenTableDialog(true); handleCloseMenu(); }} sx={{ gap: 1.5, py: 1.1, px: 2 }}>
                    <TableChartOutlinedIcon sx={{ fontSize: 20, color: '#2f9e6f' }} />
                    <Typography sx={{ fontSize: '0.9rem' }}>{t('files.addTableFiles', 'Add Table files')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => { setOpenOcrDialog(true); handleCloseMenu(); }} sx={{ gap: 1.5, py: 1.1, px: 2 }}>
                    <DocumentScannerOutlinedIcon sx={{ fontSize: 20, color: '#8257d6' }} />
                    <Typography sx={{ fontSize: '0.9rem' }}>{t('files.addOcrDocuments', 'Add OCR Documents')}</Typography>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={() => { setOpenUrlDialog(true); handleCloseMenu(); }} sx={{ gap: 1.5, py: 1.1, px: 2 }}>
                    <LinkIcon sx={{ fontSize: 20, color: '#d98324' }} />
                    <Typography sx={{ fontSize: '0.9rem' }}>{t('files.addUrl', 'Add URL')}</Typography>
                </MenuItem>
            </Menu>

            {/* Add Documents Dialog */}
            <Dialog open={openDocsDialog} onClose={closeDocs} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
                <ModalHeader
                    icon={<DescriptionOutlinedIcon />}
                    title={t('files.addDocuments', 'Add Documents')}
                    subtitle={t('files.addDocumentsSubtitle', 'Upload documents to your knowledge base')}
                    onClose={closeDocs}
                />
                <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
                    <UploadZone
                        rootProps={getRootProps()}
                        inputProps={getInputProps()}
                        isDragActive={isDragActive}
                        title={t('files.dragDrop')}
                        dropTitle={t('files.dropHere', 'Drop files here')}
                        formats={['PDF', 'TXT', 'DOCX']}
                        hint={t('files.documentsAcceptedTypes')}
                                            />
                    <SelectedFiles files={files} progress={progress} uploading={uploading} onRemove={removeFile} t={t} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
                    <Button onClick={closeDocs} sx={cancelBtnSx}>{t('files.cancel')}</Button>
                    <Button onClick={handleUpload} disabled={!files.length || uploading} sx={primaryBtnSx}>
                        {uploading ? t('files.uploading') : t('files.upload')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Table Files Dialog */}
            <Dialog open={openTableDialog} onClose={closeTable} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
                <ModalHeader
                    icon={<TableChartOutlinedIcon />}
                    title={t('files.addTableFiles', 'Add Table files')}
                    subtitle={t('files.addTableFilesSubtitle', 'Upload spreadsheets and structured data')}
                    onClose={closeTable}
                />
                <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
                    <UploadZone
                        rootProps={getTableRootProps()}
                        inputProps={getTableInputProps()}
                        isDragActive={isTableDragActive}
                        title={t('files.tableDragDrop')}
                        dropTitle={t('files.dropHere', 'Drop files here')}
                        formats={['CSV', 'XLSX']}
                        hint={t('files.tableAcceptedTypes')}
                                            />
                    <SelectedFiles files={filesTable} progress={progressTable} uploading={uploadingTable} onRemove={removeFileTable} t={t} />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            component="a"
                            href={`${process.env.PUBLIC_URL || ''}/table-template.xlsx`}
                            download="table-template.xlsx"
                            startIcon={<DownloadOutlinedIcon />}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: BRAND, borderRadius: '8px' }}
                        >
                            {t('files.downloadTableTemplate', 'Download template')}
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
                    <Button onClick={closeTable} sx={cancelBtnSx}>{t('files.cancel')}</Button>
                    <Button onClick={handleUploadTable} disabled={!filesTable.length || uploadingTable} sx={primaryBtnSx}>
                        {uploadingTable ? t('files.uploading') : t('files.upload')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add OCR Documents Dialog */}
            <Dialog open={openOcrDialog} onClose={closeOcr} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
                <ModalHeader
                    icon={<DocumentScannerOutlinedIcon />}
                    title={t('files.addOcrDocuments', 'Add OCR Documents')}
                    subtitle={t('files.addOcrDocumentsSubtitle', 'Extract text from scanned PDFs with OCR')}
                    onClose={closeOcr}
                />
                <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
                    <UploadZone
                        rootProps={getOcrRootProps()}
                        inputProps={getOcrInputProps()}
                        isDragActive={isOcrDragActive}
                        title={t('files.dragDrop')}
                        dropTitle={t('files.dropHere', 'Drop files here')}
                        formats={['PDF']}
                        hint={t('files.ocrPdfOnly')}
                                            />
                    <SelectedFiles files={filesOcr} progress={progressOcr} uploading={uploadingOcr} onRemove={removeFileOcr} t={t} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
                    <Button onClick={closeOcr} sx={cancelBtnSx}>{t('files.cancel')}</Button>
                    <Button onClick={handleUploadOcr} disabled={!filesOcr.length || uploadingOcr} sx={primaryBtnSx}>
                        {uploadingOcr ? t('files.uploading') : t('files.uploadOcr')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add URL Dialog */}
            <Dialog open={openUrlDialog} onClose={closeUrl} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
                <ModalHeader
                    icon={<LinkIcon />}
                    title={t('files.addUrl', 'Add URL')}
                    subtitle={t('files.addUrlSubtitle', 'Import content from web pages into your knowledge base')}
                    onClose={closeUrl}
                />
                <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#2b3a4b', mb: 0.75 }}>
                        {t('files.urlLabel', 'Web addresses')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        maxRows={12}
                        type="url"
                        placeholder={[
                            'https://example1.com/page',
                            'https://example2.com/page',
                            'https://example3.com/page',
                        ].join('\n')}
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        error={invalidUrlCount > 0}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                backgroundColor: '#f7f9fc',
                                fontSize: '0.9rem',
                                fontFamily: 'monospace',
                                '& fieldset': { borderColor: '#cdd7e3' },
                                '&:hover fieldset': { borderColor: BRAND },
                                '&.Mui-focused fieldset': { borderColor: BRAND, borderWidth: '1.5px' },
                            },
                            '& .MuiInputBase-inputMultiline': { resize: 'vertical' },
                        }}
                    />

                    {/* Live feedback / helper row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1, minHeight: 24 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                            {t('files.urlHelper', 'Enter one URL per line.')}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {urlLines.length > 0 && (
                            <>
                                <Chip
                                    size="small"
                                    icon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                                    label={t('files.urlValidCount', { count: validUrlCount, defaultValue: `${validUrlCount} valid` })}
                                    sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, color: '#2f9e6f', backgroundColor: 'rgba(47,158,111,0.12)', '& .MuiChip-icon': { color: '#2f9e6f' } }}
                                />
                                {invalidUrlCount > 0 && (
                                    <Chip
                                        size="small"
                                        label={t('files.urlInvalidCount', { count: invalidUrlCount, defaultValue: `${invalidUrlCount} invalid` })}
                                        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, color: '#c0392b', backgroundColor: 'rgba(224,85,78,0.12)' }}
                                    />
                                )}
                            </>
                        )}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label={t('files.refresh', 'Refresh')}
                            value={urlRefresh}
                            onChange={(e) => setUrlRefresh(e.target.value)}
                            helperText={t('files.refreshHelper', 'How often we re-fetch content from these URLs.')}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        >
                            <MenuItem value="Never">{t('files.never')}</MenuItem>
                            <MenuItem value="Daily">{t('files.daily')}</MenuItem>
                            <MenuItem value="Weekly">{t('files.weekly')}</MenuItem>
                            <MenuItem value="Monthly">{t('files.monthly')}</MenuItem>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
                    <Button onClick={closeUrl} sx={cancelBtnSx}>{t('files.cancel')}</Button>
                    <Button
                        onClick={handleAddUrl}
                        disabled={addingUrl || validUrlCount === 0 || invalidUrlCount > 0}
                        sx={primaryBtnSx}
                    >
                        {addingUrl ? t('files.addingUrl', 'Adding...') : t('files.addUrl')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar notification */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

        </Box>
    );
}

export default MultipleFileUpload;
